import { useCallback } from 'react';

// Helper function to handle API responses, reducing repetition
async function handleApiResponse(response, successMessage, errorMessagePrefix, showPopup, hidePopup, popupSize = 'small') {
  if (response.ok) {
    const data = await response.json();
    if (data.success === false) { // Check for backend-reported failure even with 2xx status
        throw new Error(data.message || `${errorMessagePrefix}: Odpowiedź serwera wskazuje na błąd (success: false).`);
    }
    hidePopup(); // Hide loading popup
    if (successMessage) {
        showPopup({ message: successMessage, type: 'success', size: popupSize });
    }
    return data; // Return data on success
  } else {
    // Handle HTTP errors (non-2xx status)
    let errorData = { message: `${errorMessagePrefix}: Błąd serwera (status ${response.status})` };
    try {
      const parsedError = await response.json();
      errorData.message = parsedError.message || errorData.message;
      // Include interactionId if present in error response (for AI logs)
      if (parsedError.interactionId) {
          errorData.interactionId = parsedError.interactionId;
      }
    } catch (e) {
       // If parsing JSON fails, try to get text response
       try {
           const errorText = await response.text();
           errorData.message = errorText || errorData.message;
       } catch (textErr) { /* Ignore text reading error */ }
    }
    throw errorData; // Throw an object containing message and potentially interactionId
  }
}


export function useOrderDetailsActions(
    orderId,
    backendUrl,
    data, // { order, files, editedQuantities, guestsTextMap } from useOrderDetailsData
    uiState, // { showPopup, hidePopup, setCopySuccessMessage, setAiStateMap, setCurrentLogs, setLogsLoading, setDeletingFileId, setDuplicatingFileId, setAddingProjectFromTemplateId, setIsUploading, aiRequestText, handleShowMetadata } from useOrderDetailsUIState
    dataUpdaters, // { setFiles, setPdfMetadatas, setEditedQuantities, setOrder } from useOrderDetailsData
    navigate // from react-router-dom
) {

  const { order, files, editedQuantities, guestsTextMap } = data;
  const {
    showPopup, hidePopup, setCopySuccessMessage, setAiStateMap, setCurrentLogs, setLogsLoading,
    setDeletingFileId, setDuplicatingFileId, setAddingProjectFromTemplateId, setIsUploading,
    // aiRequestText, // Removed - text will be passed as argument
    handleShowMetadata: uiHandleShowMetadata // Rename to avoid conflict
  } = uiState;
  // Destructure fetchOrderData from dataUpdaters
  const { setFiles, setPdfMetadatas, setEditedQuantities, setOrder, fetchOrderData } = dataUpdaters;

  // --- File Upload ---
  const handleFileDropped = useCallback(async ({ base64, name }) => {
    setIsUploading(true);
    showPopup({ title: "Upload", message: "Przesyłanie pliku...", type: 'loading' });

    try {
      const base64data = base64.split(",")[1] || "";
      const binary = atob(base64data);
      const array = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) {
        array[i] = binary.charCodeAt(i);
      }
      const blob = new Blob([array], { type: "application/octet-stream" });
      const formData = new FormData();
      formData.append("file", blob, name);

      const res = await fetch(`${backendUrl}/admin/orders/${orderId}/files`, {
        method: "POST",
        credentials: "include",
        body: formData
      });

      const result = await handleApiResponse(res, "Plik został dodany.", "Błąd podczas przesyłania pliku", showPopup, hidePopup);

      const addedFiles = result.files || [];
      setFiles(addedFiles);
      // Update quantities for new files
      const newQuantities = { ...editedQuantities };
      addedFiles.forEach(file => {
        if (!(file.id in newQuantities)) {
          newQuantities[file.id] = file.quantity ?? 1;
        }
      });
      setEditedQuantities(newQuantities);
      setPdfMetadatas(result.pdfMetadatas || []);

    } catch (err) {
      console.error("[handleFileDropped]", err);
      hidePopup(); // Ensure loading is hidden on error
      showPopup({ title: "Błąd uploadu", message: err.message || "Nie udało się dodać pliku.", type: 'error', size: 'small' });
    } finally {
      setIsUploading(false);
    }
  }, [orderId, backendUrl, showPopup, hidePopup, setIsUploading, setFiles, setPdfMetadatas, editedQuantities, setEditedQuantities]);

  // --- Show Metadata ---
   const handleShowMetadata = useCallback(async (downloadKey) => {
    showPopup({ title: "Metadane", message: "Pobieranie metadanych...", type: 'loading' });
    try {
      const metaUrl = `${backendUrl}/admin/orders/${orderId}/pdf-metadata?downloadKey=${downloadKey}`;
      const r = await fetch(metaUrl, { credentials: "include" });
      const data = await handleApiResponse(r, null, "Błąd pobierania metadanych PDF", showPopup, hidePopup); // No success message needed

      if (!data.pdfData) { // Specific check for pdfData presence
          throw new Error("Brak pdfData w odpowiedzi serwera.");
      }
      uiHandleShowMetadata(data.pdfData); // Call the UI state handler
      hidePopup(); // Hide loading popup manually as no success message is shown by handleApiResponse
    } catch (err) {
      console.error("[handleShowMetadata]", err);
      hidePopup();
      showPopup({ title: "Błąd metadanych", message: err.message || "Nie udało się pobrać metadanych PDF.", type: 'error', size: 'small' });
    }
  }, [orderId, backendUrl, showPopup, hidePopup, uiHandleShowMetadata]);

  // --- Generate Production PDF ---
  // Define doGeneratePdf FIRST because handleGeneratePdf depends on it in useCallback
  const doGeneratePdf = useCallback(async (downloadKey, bleedValueMm, printMode) => {
    // Restore loading popup, ensuring it's distinct if necessary
    const modeLabel = printMode === 'uv' ? 'UV (Pantone)' : 'złoceń';
    showPopup({ title: "Generowanie PDF", message: `Trwa generowanie PDF ${modeLabel}, proszę czekać...`, type: 'loading', size: 'compact' });
    
    // Build URL with printMode parameter
    let genUrl = `${backendUrl}/admin/orders/${orderId}/generate-pdf?downloadKey=${downloadKey}&printMode=${printMode}`;
    if (bleedValueMm > 0) {
      genUrl += `&bleed=${bleedValueMm}`;
      console.log(`[doGeneratePdf] Generating ${modeLabel} PDF with bleed: ${bleedValueMm}mm`);
    } else {
      console.log(`[doGeneratePdf] Generating ${modeLabel} PDF without bleed (bleed=${bleedValueMm}mm)`);
    }

    try {
      const res = await fetch(genUrl, { method: "POST", credentials: "include" });
      const successMessage = bleedValueMm > 0
        ? `PDF produkcyjny (${modeLabel}) ze spadem ${bleedValueMm}mm został wygenerowany.`
        : `PDF produkcyjny (${modeLabel}) bez spadu został wygenerowany.`;
      const data = await handleApiResponse(res, successMessage, "Błąd generowania PDF", showPopup, hidePopup, 'compact');

      const updatedFiles = data.files || [];
      if (updatedFiles.length > 0) { // Check if files array is not empty
        setFiles(updatedFiles);
        // Update quantities
        const newQuantities = { ...editedQuantities };
        updatedFiles.forEach(file => {
          newQuantities[file.id] = file.quantity ?? 1;
        });
        setEditedQuantities(newQuantities);
      }
    } catch (err) {
      console.error("[doGeneratePdf]", err);
      hidePopup();
      showPopup({ title: "Błąd generowania PDF", message: err.message || "Nie udało się wygenerować nowego PDF produkcyjnego.", type: 'error', size: 'small' });
    }
  }, [orderId, backendUrl, showPopup, hidePopup, setFiles, editedQuantities, setEditedQuantities]);

  // Define handleGeneratePdf AFTER doGeneratePdf
  const handleGeneratePdf = useCallback((downloadKey) => {
    if (!downloadKey) {
      showPopup({ title: "Błąd", message: "Brak klucza do pobrania pliku (downloadKey).", type: 'error', size: 'compact' });
      return;
    }
    // Show popup to ask for bleed value and print mode
    showPopup({
      title: "PDF produkcyjny",
      message: "Wybierz tryb druku i podaj wartość spadu:",
      type: 'input',
      inputType: 'number',
      inputPlaceholder: 'np. 2.5',
      initialInputValue: '2.5',
      radioOptions: [
        { value: 'gold', label: 'Złocenia (osobne strony, kolor czarny)' },
        { value: 'uv', label: 'Druk UV (te same strony, kolor Pantone)' }
      ],
      initialRadioValue: 'gold', // Domyślnie złocenia
      confirmText: 'Generuj PDF',
      size: 'compact',
      onConfirm: (values) => {
        // values = { input, radio } gdy są radio buttons
        const bleedValue = parseFloat(values.input);
        const printMode = values.radio;
        
        if (isNaN(bleedValue) || bleedValue < 0) {
          showPopup({ title: "Błąd", message: "Nieprawidłowa wartość spadu. Wprowadź liczbę większą lub równą 0.", type: 'error', size: 'compact' });
          return;
        }
        
        if (!printMode || (printMode !== 'gold' && printMode !== 'uv')) {
          showPopup({ title: "Błąd", message: "Wybierz tryb druku.", type: 'error', size: 'compact' });
          return;
        }
        
        doGeneratePdf(downloadKey, bleedValue, printMode); // Call the function defined above
      },
    });
  }, [showPopup, doGeneratePdf]);

  // --- Preview PDF ---
  const handlePreviewPdf = useCallback(async (downloadKey, setShowEditorOverlay, setEditorOverlayProps) => {
    showPopup({ title: "Generowanie podglądu", message: "Proszę czekać...", type: 'loading', size: 'compact' });
    
    try {
      // 1. Pobierz informacje o projekcie, aby uzyskać projectId
      const fileObj = files.find(f => f.downloadKey === downloadKey);
      if (!fileObj) {
        throw new Error("Nie znaleziono pliku o podanym kluczu pobierania.");
      }
      const projectId = fileObj.id;

      // 2. Pobierz najnowszą historię projektu, aby sprawdzić, czy jest już wycentrowany
      const historyUrl = `${backendUrl}/admin/projects/${projectId}/history/latest`;
      console.log("[handlePreviewPdf] => GET history =>", historyUrl);
      const historyRes = await fetch(historyUrl, { credentials: "include" });
      if (!historyRes.ok) {
        throw new Error(`Błąd pobierania historii projektu (status=${historyRes.status})`);
      }
      const historyData = await historyRes.json();
      
      // 3. Sprawdź, czy projekt jest już wycentrowany - zachowujemy tylko tę informację
      if (historyData.autoCentered) {
        console.log("[handlePreviewPdf] => Project already auto-centered, generating PDF directly");
      } else {
        // 4. Sprawdź, czy projekt wymaga autocentrowania (ma pola z flagą personalize) - tylko do logów
        const hasPersonalization = (historyData.jsonData?.pages || []).some(page => 
          (page.texts || []).some(text => text.flags?.personalize && text.alignment === 'center')
        );
        
        if (hasPersonalization) {
          console.log("[handlePreviewPdf] => Project needs auto-centering, but skipping as requested");
        } else {
          console.log("[handlePreviewPdf] => Project doesn't need auto-centering");
        }
      }
      
      // Zawsze generuj PDF bezpośrednio, niezależnie od stanu autocentrowania
      await generatePdf(downloadKey);
    } catch (err) {
      console.error("[handlePreviewPdf]", err);
      hidePopup();
      showPopup({ title: "Błąd podglądu", message: err.message || "Nie udało się wygenerować podglądu PDF.", type: 'error', size: 'small' });
    }
  }, [orderId, backendUrl, showPopup, hidePopup, files]);

  // Funkcja pomocnicza do generowania PDF
  const generatePdf = async (downloadKey) => {
    try {
      const previewUrl = `${backendUrl}/admin/orders/${orderId}/preview-pdf?downloadKey=${downloadKey}`;
      const res = await fetch(previewUrl, { method: "GET", credentials: "include" });
      if (!res.ok) { // handleApiResponse is not suitable for blob responses
        throw new Error(`Błąd generowania podglądu (status=${res.status})`);
      }
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = "podglad.pdf";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(blobUrl);
      hidePopup(); // Hide loading popup on success
    } catch (err) {
      console.error("[generatePdf]", err);
      hidePopup();
      showPopup({ title: "Błąd podglądu", message: err.message || "Nie udało się wygenerować podglądu PDF.", type: 'error', size: 'small' });
    }
  };

  // --- Save File Changes (Status/Quantity) ---
  const handleSaveFileChanges = useCallback(async (fileId, newStatusIdForSave = undefined) => { // Add newStatusIdForSave
    const fileToSave = files.find(f => f.id === fileId);
    if (!fileToSave) return;

    let quantityToSave = editedQuantities[fileId];
    if (quantityToSave === "") {
      quantityToSave = 0;
    } else {
      quantityToSave = parseInt(quantityToSave, 10);
      if (isNaN(quantityToSave) || quantityToSave < 0) {
        quantityToSave = fileToSave.quantity ?? 1;
        setEditedQuantities(prev => ({ ...prev, [fileId]: quantityToSave })); // Correct UI state
      }
    }

    // Prepare the updated file object, including status if provided
    const updatedFileObject = { 
      ...fileToSave, 
      quantity: quantityToSave 
    };

    if (newStatusIdForSave !== undefined) {
      updatedFileObject.statusId = newStatusIdForSave;
    }

    const filesPayload = files.map(f => (f.id === fileId ? updatedFileObject : f));

    // Use a silent loading state or skip popup for frequent saves? For now, keep it.
    showPopup({ title: "Zapisywanie zmian", message: "Proszę czekać...", type: 'loading' });

    const body = {
      orderName: order?.orderName,
      customOrderId: order?.email,
      files: filesPayload
    };

    try {
      const res = await fetch(`${backendUrl}/admin/orders/${orderId}`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body)
      });
      // Use handleApiResponse, but suppress the success message for less noise
      const result = await handleApiResponse(res, null, "Błąd zapisu zmian pliku", showPopup, hidePopup);

      const refreshedFiles = result.order?.files || files; // Get the updated files array from the response
      setFiles(refreshedFiles); // Update the main files state

      // Reset editedQuantities based entirely on the refreshed data from the server
      // This ensures the UI reflects the actual saved state.
      setEditedQuantities(() => {
          const newQuantities = {};
          refreshedFiles.forEach(file => {
              newQuantities[file.id] = file.quantity ?? 1;
          });
          return newQuantities;
      });


    } catch (err) {
      console.error("[handleSaveFileChanges]", err);
      hidePopup();
      showPopup({ title: "Błąd zapisu", message: err.message || "Nie udało się zapisać zmian.", type: 'error', size: 'small' });
    }
  }, [files, editedQuantities, order, backendUrl, orderId, showPopup, hidePopup, setFiles, setEditedQuantities]);

  // --- Handle Status Change (Updates local state and triggers save) ---
   const handleStatusChange = useCallback((fileId, newStatusId) => {
    const newFiles = files.map((f) => {
      if (f.id === fileId) {
        const currentStatusId = f.statusId ?? "";
        const targetStatusId = newStatusId ? String(newStatusId) : "";
        if (currentStatusId !== targetStatusId) {
          return { ...f, statusId: newStatusId ? Number(newStatusId) : null };
        }
      }
      return f;
    });

    if (JSON.stringify(newFiles) !== JSON.stringify(files)) {
        setFiles(newFiles); // Update local state first
        // Pass the newStatusId to handleSaveFileChanges
        const statusToSave = newStatusId ? Number(newStatusId) : null;
        handleSaveFileChanges(fileId, statusToSave); 
    }
  }, [files, setFiles, handleSaveFileChanges]);


  // --- Guest List Actions ---
  const handleFetchGuestsForFile = useCallback(async (fileObj) => {
    const projectId = fileObj.id;
    if (!projectId) {
      showPopup({ title: "Błąd", message: "Brak ID projektu (file.id) – nie można pobrać listy gości.", type: 'error', size: 'small' });
      return;
    }
    uiState.setGuestsLoadingMap((prev) => ({ ...prev, [projectId]: true }));
    const url = `${backendUrl}/admin/projects/${projectId}/history/latest`;
    try {
      const r = await fetch(url, { credentials: "include" });
      // Don't use handleApiResponse here as success doesn't need a popup
      if (!r.ok) throw new Error(`Błąd pobierania JSON (status=${r.status}).`);
      const data = await r.json();
      if (!data.jsonData) throw new Error("Brak jsonData w odpowiedzi.");

      let personalizacja = [];
      const pages = data.jsonData.pages || [];
      let personalizePageIndex = pages.findIndex(pg =>
        (pg.texts || []).some(t => t.flags?.personalize) ||
        (pg.images || []).some(im => im.flags?.personalize) ||
        (pg.svgs || []).some(sv => sv.flags?.personalize)
      );

      if (personalizePageIndex >= 0 && Array.isArray(pages[personalizePageIndex].personalizacja)) {
        personalizacja = pages[personalizePageIndex].personalizacja;
      } else {
         console.warn(`[handleFetchGuestsForFile][Admin] No page with personalize flag or no 'personalizacja' array found.`);
      }
      uiState.setGuestsTextMap((prev) => ({ ...prev, [projectId]: personalizacja.join("\n") }));
    } catch (err) {
      console.error("[handleFetchGuestsForFile]", err);
      showPopup({ title: "Błąd pobierania gości", message: err.message || "Nie udało się pobrać listy gości.", type: 'error', size: 'small' });
    } finally {
      uiState.setGuestsLoadingMap((prev) => ({ ...prev, [projectId]: false }));
    }
  }, [backendUrl, showPopup, uiState]); // Added uiState dependency

  const handleSaveGuestsForFile = useCallback(async (fileObj) => {
    const projectId = fileObj.id;
    if (!projectId) {
      showPopup({ title: "Błąd", message: "Brak ID projektu (file.id) – nie można zapisać listy gości.", type: 'error', size: 'small' });
      return;
    }
    uiState.setGuestsLoadingMap((prev) => ({ ...prev, [projectId]: true }));
    showPopup({ title: "Zapisywanie listy gości", message: "Proszę czekać...", type: 'loading', size: 'compact' });

    try {
      // 1. Fetch latest history
      const getUrl = `${backendUrl}/admin/projects/${projectId}/history/latest`;
      const getRes = await fetch(getUrl, { credentials: "include" });
      if (!getRes.ok) throw new Error(`Błąd pobierania JSON (status=${getRes.status}).`);
      const getData = await getRes.json();
      if (!getData.jsonData) throw new Error("Brak jsonData (przed zapisem).");

      // 2. Prepare updated pages data
      let pages = getData.jsonData.pages || [];
      const lines = (guestsTextMap[projectId] || "").split("\n").map(l => l.trim()).filter(Boolean);
      let personalizePageIndex = pages.findIndex(pg =>
        (pg.texts || []).some(t => t.flags?.personalize) ||
        (pg.images || []).some(im => im.flags?.personalize) ||
        (pg.svgs || []).some(sv => sv.flags?.personalize)
      );

      if (personalizePageIndex >= 0) {
        pages = pages.map((pg, idx) => idx === personalizePageIndex ? { ...pg, personalizacja: lines } : pg);
      } else {
        console.error("[handleSaveGuestsForFile][Admin] Could not find page with personalize flag. Save failed.");
        throw new Error("Nie znaleziono strony z flagą personalizacji w projekcie.");
      }

      // 3. Save updated history
      const finalJson = { pages };
      const saveUrl = `${backendUrl}/admin/projects/${projectId}/history`;
      const saveRes = await fetch(saveUrl, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ jsonData: finalJson, saveType: "manual" })
      });

      // Use handleApiResponse for saving part
      await handleApiResponse(saveRes, "Lista gości została zapisana.", "Błąd zapisu JSON", showPopup, hidePopup);

    } catch (err) {
      console.error("[handleSaveGuestsForFile]", err);
      hidePopup(); // Ensure loading is hidden
      showPopup({ title: "Błąd zapisu gości", message: err.message || "Nie udało się zapisać listy gości.", type: 'error', size: 'small' });
    } finally {
      uiState.setGuestsLoadingMap((prev) => ({ ...prev, [projectId]: false }));
    }
  }, [backendUrl, guestsTextMap, showPopup, hidePopup, uiState]); // Added uiState dependency

  // --- Generate Personalized Project ---
  const handleGeneratePersonalizedProject = useCallback((fileObj) => {
    const existingChild = files.find((f) => f.parentFileId === fileObj.id);
    if (existingChild) {
      showPopup({
        title: "Uwaga!",
        message: <>Projekt z listą gości już istnieje (<strong>{existingChild.fileName}</strong>).<br />Czy chcesz go nadpisać? Poprzedni plik zostanie usunięty.</>,
        type: 'confirm',
        confirmText: 'Tak, nadpisz',
        size: 'compact',
        onConfirm: () => doGeneratePersonalized(fileObj, existingChild),
      });
    } else {
      doGeneratePersonalized(fileObj, null);
    }
  }, [files, showPopup]); // Removed doGeneratePersonalized dependency

  const doGeneratePersonalized = useCallback(async (fileObj, existingChild) => {
    showPopup({ title: "Generowanie projektu", message: "Proszę czekać...", type: 'loading', size: 'compact' });
    try {
      const url = `${backendUrl}/admin/orders/${orderId}/personalize?downloadKey=${fileObj.downloadKey}`;
      const body = { parentFileId: fileObj.id, childFileId: existingChild?.id || null };
      const res = await fetch(url, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body)
      });

      const data = await handleApiResponse(res, "Wygenerowano projekt z listą gości.", "Błąd generowania projektu z listą gości", showPopup, hidePopup, 'compact');

      if (data.files) {
        const updatedFiles = data.files || [];
        setFiles(updatedFiles);
        const newQuantities = { ...editedQuantities };
        updatedFiles.forEach(file => { newQuantities[file.id] = file.quantity ?? 1; });
        setEditedQuantities(newQuantities);
      }
      if (data.pdfMetadatas) {
        setPdfMetadatas(data.pdfMetadatas);
      }
    } catch (err) {
      console.error("[doGeneratePersonalized]", err);
      hidePopup();
      showPopup({ title: "Błąd generowania", message: err.message || "Nie udało się wygenerować projektu spersonalizowanego.", type: 'error', size: 'small' });
    }
  }, [orderId, backendUrl, showPopup, hidePopup, setFiles, editedQuantities, setEditedQuantities, setPdfMetadatas]);

  // --- Delete Project ---
  // Define doDeleteProject BEFORE handleDeleteProject
  const doDeleteProject = useCallback(async (fileObj) => {
    if (!fileObj) return;
    console.log(`[doDeleteProject] Starting deletion for file: ${fileObj.fileName} (ID: ${fileObj.id})`); // Debug log
    setDeletingFileId(fileObj.id);
    showPopup({ title: "Usuwanie projektu", message: "Proszę czekać...", type: 'loading' });
    try {
      const url = `${backendUrl}/admin/orders/${orderId}/projects/${fileObj.id}`;
      console.log(`[doDeleteProject] Sending DELETE request to: ${url}`); // Debug log
      const res = await fetch(url, { method: "DELETE", credentials: "include" });
      await handleApiResponse(res, "Projekt został pomyślnie usunięty.", "Błąd usuwania projektu (admin)", showPopup, hidePopup);

      // Update local state on success
      setFiles(prevFiles => prevFiles.filter(f => f.id !== fileObj.id));
      setEditedQuantities(prev => {
        const newState = { ...prev };
        delete newState[fileObj.id];
        return newState;
      });

    } catch (err) {
      console.error("[doDeleteProject][admin]", err);
      hidePopup();
      showPopup({ title: "Błąd usuwania", message: err.message || "Wystąpił błąd podczas próby usunięcia projektu.", type: 'error', size: 'small' });
    } finally {
      setDeletingFileId(null);
    }
  }, [orderId, backendUrl, showPopup, hidePopup, setDeletingFileId, setFiles, setEditedQuantities]);

  const handleDeleteProject = useCallback((fileObj) => {
    if (!fileObj) return;
    console.log(`[handleDeleteProject] Called for file: ${fileObj.fileName} (ID: ${fileObj.id})`); // Debug log
    const confirmCallback = () => {
        console.log(`[handleDeleteProject] onConfirm called for file: ${fileObj.fileName}`); // Debug log
        doDeleteProject(fileObj); // Now doDeleteProject is guaranteed to be defined
    };
    showPopup({
      title: "Potwierdzenie usunięcia",
      message: <>Czy na pewno chcesz trwale usunąć projekt <strong>{fileObj.fileName}</strong>? Tej operacji nie można cofnąć.</>,
      type: 'confirm',
      confirmText: 'Tak, usuń',
      size: 'medium',
      onConfirm: confirmCallback, // Use the wrapped callback
      onCancel: () => console.log(`[handleDeleteProject] Delete cancelled for file: ${fileObj.fileName}`) // Optional: log cancellation
    });
  }, [showPopup, doDeleteProject]); // Keep doDeleteProject dependency

  // --- Duplicate Project ---
  const handleDuplicateProject = useCallback(async (fileObj) => {
    if (!fileObj) return;
    setDuplicatingFileId(fileObj.id);
    showPopup({ title: "Duplikowanie projektu", message: "Proszę czekać...", type: 'loading' });
    try {
      const url = `${backendUrl}/admin/orders/${orderId}/duplicate-project`;
      const res = await fetch(url, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ fileId: fileObj.id })
      });
      const result = await handleApiResponse(res, "Projekt został pomyślnie zduplikowany.", "Błąd duplikowania projektu (admin)", showPopup, hidePopup);

      const updatedFiles = result.files || [];
      setFiles(updatedFiles);
      const newQuantities = { ...editedQuantities };
      updatedFiles.forEach(file => { if (!(file.id in newQuantities)) newQuantities[file.id] = file.quantity ?? 1; });
      setEditedQuantities(newQuantities);

    } catch (err) {
      console.error("[handleDuplicateProject][admin]", err);
      hidePopup();
      showPopup({ title: "Błąd duplikowania", message: err.message || "Wystąpił błąd podczas próby duplikowania projektu.", type: 'error', size: 'small' });
    } finally {
      setDuplicatingFileId(null);
    }
  }, [orderId, backendUrl, showPopup, hidePopup, setDuplicatingFileId, setFiles, editedQuantities, setEditedQuantities]);

  // --- Add Project From Template ---
  const handleAddProjectFromTemplate = useCallback(async (templateId) => {
    if (!templateId) return;
    setAddingProjectFromTemplateId(templateId);
    showPopup({ title: "Dodawanie projektu", message: "Proszę czekać...", type: 'loading' });
    try {
      const url = `${backendUrl}/admin/orders/${orderId}/add-project-from-template`;
      const res = await fetch(url, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ templateId })
      });
      const result = await handleApiResponse(res, "Projekt został pomyślnie dodany z szablonu.", "Błąd dodawania projektu z szablonu (admin)", showPopup, hidePopup);

      const updatedFiles = result.files || [];
      setFiles(updatedFiles);
      const newQuantities = { ...editedQuantities };
      updatedFiles.forEach(file => { if (!(file.id in newQuantities)) newQuantities[file.id] = file.quantity ?? 1; });
      setEditedQuantities(newQuantities);

    } catch (err) {
      console.error("[handleAddProjectFromTemplate][admin]", err);
      hidePopup();
      showPopup({ title: "Błąd dodawania", message: err.message || "Wystąpił błąd podczas próby dodania projektu z szablonu.", type: 'error', size: 'small' });
    } finally {
      setAddingProjectFromTemplateId(null);
    }
  }, [orderId, backendUrl, showPopup, hidePopup, setAddingProjectFromTemplateId, setFiles, editedQuantities, setEditedQuantities]);

  // --- Save Email ---
  const handleSaveEmail = useCallback(async (newEmail) => {
    if (!order || newEmail === order.email) {
      uiState.setIsEditingEmail(false); // Just close edit mode if no change
      return;
    }
    showPopup({ title: "Zapisywanie emaila", message: "Proszę czekać...", type: 'loading' });
    const body = { customOrderId: newEmail }; // Assuming customOrderId is the email field
    try {
      const res = await fetch(`${backendUrl}/admin/orders/${orderId}`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body)
      });
      // resultFromPut będzie zawierać odpowiedź z serwera po operacji PUT
      const resultFromPut = await handleApiResponse(res, "Adres e-mail został zaktualizowany.", "Błąd zapisu emaila", showPopup, hidePopup);

      // Użyj resultFromPut do aktualizacji stanu, jeśli zawiera zaktualizowane zamówienie
      if (resultFromPut && resultFromPut.order) {
          console.log("[handleSaveEmail] Updating order state directly from PUT response.");
          dataUpdaters.setOrder(resultFromPut.order);
          // Upewnij się, że pole email w stanie edycji jest również zaktualizowane
          // na podstawie tego, co zwrócił serwer.
          dataUpdaters.setEditedEmail(resultFromPut.order.customOrderId || "");
      } else {
          // Fallback do fetchOrderData, jeśli odpowiedź PUT nie jest wystarczająca lub brakuje pola 'order'
          console.warn("[handleSaveEmail] PUT response did not contain order object, falling back to fetchOrderData.");
          if (dataUpdaters.fetchOrderData) {
              console.log("[handleSaveEmail] Refetching order data after successful save (fallback)...");
              try {
                  await dataUpdaters.fetchOrderData(); // Wait for the refetch to complete
                  console.log("[handleSaveEmail] Order data refetched successfully (fallback).");
              } catch (refetchError) {
                   console.error("[handleSaveEmail] Refetch failed after save (fallback):", refetchError);
              }
          } else {
              console.error("[handleSaveEmail] fetchOrderData function is missing from dataUpdaters! Cannot guarantee state consistency on fallback.");
              // W ostateczności, jeśli nie ma fetchOrderData, zaktualizuj przynajmniej to co wiemy
              dataUpdaters.setOrder(currentOrder => ({ ...currentOrder, customOrderId: newEmail, email: newEmail }));
              dataUpdaters.setEditedEmail(newEmail);
          }
      }

      // Zamknij tryb edycji. Stan `editedEmail` w `dataState` powinien być już aktualny.
      // Stan `editedEmail` w `uiState` nie jest bezpośrednio używany do wyświetlania wartości w OrderInfoSection,
      // więc jego aktualizacja tutaj jest mniej krytyczna, ale dla spójności można by go zsynchronizować.
      // uiState.setEditedEmail(newEmail); // Można rozważyć usunięcie lub zsynchronizowanie z dataState.editedEmail
      uiState.setIsEditingEmail(false); // Close the editing UI

    } catch (err) {
      console.error("[handleSaveEmail]", err);
      hidePopup();
      showPopup({ title: "Błąd zapisu", message: err.message || "Nie udało się zapisać adresu e-mail.", type: 'error', size: 'small' });
      // Don't close edit mode on error
    }
    // Depend on the dataUpdaters object instead of individual functions/states from it
  }, [orderId, backendUrl, showPopup, hidePopup, uiState, dataUpdaters]);

  // --- Copy Public Link ---
  const handleCopyPublicLink = useCallback(() => {
    if (!order || !order.publicAccessKey) {
      showPopup({ title: "Brak klucza", message: "Nie można skopiować linku, brak klucza publicznego.", type: 'error', size: 'compact' });
      return;
    }
    const publicLink = `${window.location.origin}/client/orders/${order.publicAccessKey}`;
    console.log(`[handleCopyPublicLink] Attempting to copy: ${publicLink}`); // Debug log

    // Check Clipboard API availability more robustly
    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      console.log("[handleCopyPublicLink] Using Clipboard API (navigator.clipboard.writeText)"); // Debug log
      navigator.clipboard.writeText(publicLink)
        .then(() => {
          console.log("[handleCopyPublicLink] Clipboard API success."); // Debug log
          showPopup({ 
            title: "Sukces", 
            message: "Link skopiowany do schowka", 
            type: 'success', 
            size: 'compact' 
          });
        })
        .catch(err => {
          console.error('[handleCopyPublicLink] Clipboard API error: ', err); // Debug log
          showPopup({ title: "Błąd kopiowania", message: "Nie udało się skopiować linku (API).", type: 'error', size: 'compact' });
        });
    } else {
      // Fallback for older browsers or insecure contexts
      console.warn("[handleCopyPublicLink] Clipboard API not available or writeText is not a function, using fallback execCommand."); // Updated log
      try {
        const textArea = document.createElement("textarea");
        textArea.value = publicLink;
        textArea.style.position = "fixed";
        textArea.style.left = "-9999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);

        if (successful) {
          console.log("[handleCopyPublicLink] Fallback execCommand success."); // Debug log
          showPopup({ 
            title: "Sukces", 
            message: "Link skopiowany do schowka", 
            type: 'success', 
            size: 'compact' 
          });
        } else {
          console.error('[handleCopyPublicLink] Fallback execCommand returned false.'); // Debug log
          throw new Error('document.execCommand("copy") returned false');
        }
      } catch (err) {
        console.error('[handleCopyPublicLink] Fallback execCommand error: ', err); // Debug log
        showPopup({ title: "Błąd kopiowania", message: "Nie udało się skopiować linku (fallback).", type: 'error', size: 'compact' });
      }
    }
  }, [order, showPopup]);


  // --- AI Actions ---
  const POLLING_INTERVAL = 5000; // 5 sekund
  const MAX_POLLING_ATTEMPTS = 120; // 5s * 120 = 600s = 10 minut

  const pollInteractionStatus = useCallback(async (projectId, clientInteractionId) => {
    let attempts = 0;
    const poll = async (resolve, reject) => {
      if (attempts >= MAX_POLLING_ATTEMPTS) {
        reject(new Error("Przekroczono limit czasu oczekiwania na odpowiedź serwera dotyczącą zapisu projektu."));
        return;
      }
      attempts++;
      try {
        const statusUrl = `${backendUrl}/admin/ai-interaction-status/${clientInteractionId}`;
        const statusRes = await fetch(statusUrl, { credentials: "include" });
        if (!statusRes.ok) {
          // Jeśli status HTTP jest błędem, ale nie 404 (co oznacza, że status jeszcze nie istnieje)
          if (statusRes.status !== 404) { 
            const errorText = await statusRes.text();
            console.error(`[pollInteractionStatus] Błąd HTTP ${statusRes.status} podczas odpytywania: ${errorText}`);
            // Można rozważyć ponowienie próby lub odrzucenie
          }
          // Jeśli 404 lub inny błąd, kontynuuj odpytywanie
          setTimeout(() => poll(resolve, reject), POLLING_INTERVAL);
          return;
        }
        const statusData = await statusRes.json();
        console.log(`[pollInteractionStatus] Attempt ${attempts}, Status for ${clientInteractionId}:`, statusData);

        if (statusData.status === 'completed_success') {
          resolve({ success: true, message: statusData.message || "Projekt został pomyślnie zapisany.", interactionId: clientInteractionId });
        } else if (statusData.status === 'completed_error') {
          reject(new Error(statusData.message || "Wystąpił błąd po stronie serwera podczas przetwarzania AI."));
        } else if (statusData.status === 'pending' || statusData.status === 'not_found') {
          // Kontynuuj odpytywanie jeśli 'pending' lub jeśli status jeszcze nie został utworzony ('not_found')
          setTimeout(() => poll(resolve, reject), POLLING_INTERVAL);
        } else {
          // Nieznany status, zakończ z błędem
          reject(new Error(`Nieznany status interakcji: ${statusData.status}`));
        }
      } catch (pollError) {
        console.error("[pollInteractionStatus] Błąd podczas odpytywania:", pollError);
        // Kontynuuj odpytywanie, chyba że to błąd krytyczny
        setTimeout(() => poll(resolve, reject), POLLING_INTERVAL);
      }
    };
    return new Promise(poll);
  }, [backendUrl]);

  const handleAiRequestSubmit = useCallback(async (projectId, text) => {
    if (!projectId || !text || !text.trim()) return;

    const clientInteractionId = `client-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
    setAiStateMap(prev => ({ ...prev, [projectId]: { status: 'loading', message: 'Przetwarzanie przez AI...', interactionId: clientInteractionId } }));
    uiState.handleCloseAiModal();

    try {
      const url = `${backendUrl}/admin/projects/${projectId}/ai-modify`;
      const res = await fetch(url, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_request_text: text, clientInteractionId }) // Wyślij clientInteractionId
      });

      // Jeśli odpowiedź jest 524 (lub inny timeout), lub jeśli chcemy zawsze odpytywać
      if (res.status === 524 || res.status === 504 || !res.ok) { // Dodano 504 i ogólne !res.ok dla pewności
        console.warn(`[handleAiRequestSubmit] Otrzymano status ${res.status}. Rozpoczynanie odpytywania o status zapisu dla interactionId: ${clientInteractionId}`);
        showPopup({ title: "Przetwarzanie AI", message: "Serwer przetwarza żądanie. Może to chwilę potrwać. Sprawdzanie statusu...", type: 'loading' });
        
        // Rozpocznij odpytywanie
        const pollingResult = await pollInteractionStatus(projectId, clientInteractionId);
        
        // pollingResult to { success: true, message: ..., interactionId: ... } lub rzucony błąd
        hidePopup();
        setAiStateMap(prev => ({ ...prev, [projectId]: { status: 'success', message: pollingResult.message, interactionId: clientInteractionId } }));
        showPopup({ message: pollingResult.message, type: 'success', size: 'small' });
        if (fetchOrderData) {
            console.log("[handleAiRequestSubmit] Refreshing order data after AI success (polling).");
            fetchOrderData();
        }
        return; // Zakończ, ponieważ obsłużyliśmy przez odpytywanie
      }

      // Jeśli odpowiedź była OK (np. 200), ale backend mógł nadal przetwarzać
      // Możemy również tutaj zainicjować odpytywanie lub polegać na odpowiedzi backendu
      const data = await res.json(); // handleApiResponse nie jest tu idealne, bo chcemy inną logikę

      if (data.success === false) { // Backend zgłosił błąd od razu
          throw new Error(data.message || `Błąd Komunikacji AI: Odpowiedź serwera wskazuje na błąd (success: false).`);
      }

      // Jeśli backend od razu zwrócił sukces (np. dla krótkich operacji)
      if (data.aiResponseType === 'text') {
        console.warn("[handleAiRequestSubmit] AI responded with text (direct response).");
        setAiStateMap(prev => ({ ...prev, [projectId]: { status: 'error', message: data.message, interactionId: data.interactionId || clientInteractionId } }));
        showPopup({ title: "Nietypowa odpowiedź AI", message: data.message, type: 'error', size: 'small' });
      } else {
        console.log("[handleAiRequestSubmit] AI reported success (direct response).");
        setAiStateMap(prev => ({ ...prev, [projectId]: { status: 'success', message: data.message || 'Modyfikacja AI zakończona sukcesem.', interactionId: data.interactionId || clientInteractionId } }));
        showPopup({ message: data.message || 'Modyfikacja AI zakończona sukcesem.', type: 'success', size: 'small' });
        if (fetchOrderData) {
            console.log("[handleAiRequestSubmit] Refreshing order data after AI success (direct response).");
            fetchOrderData();
        }
      }

    } catch (err) {
      console.error("[handleAiRequestSubmit] Error:", err);
      hidePopup();
      setAiStateMap(prev => ({
        ...prev,
        [projectId]: {
          status: 'error',
          message: err.message || 'Błąd przetwarzania AI.',
          interactionId: clientInteractionId // Zawsze używaj clientInteractionId dla spójności
        }
      }));
      showPopup({ title: "Błąd AI", message: err.message || 'Nie udało się przetworzyć żądania AI.', type: 'error', size: 'small' });
      // Można tutaj również zainicjować odpytywanie, jeśli błąd był np. sieciowy, a chcemy sprawdzić czy backend mimo to przetworzył
      // Ale dla uproszczenia, na razie zakładamy, że błąd oznacza koniec.
    }
  }, [backendUrl, setAiStateMap, showPopup, hidePopup, uiState, fetchOrderData, pollInteractionStatus]);

  const handleFetchAiLogs = useCallback(async (projectId, interactionId) => {
    if (!interactionId) return;

    // Loading state is set in uiState.handleShowLogs when the modal is opened
    setCurrentLogs("Pobieranie logów..."); // Initial message

    const tryFetchLog = async (type) => {
      let url = `${backendUrl}/admin/projects/${projectId}/ai-interactions/${interactionId}/log`;
      if (type && type !== 'standard') {
        url += `?type=${type}`;
      }
      console.log(`[handleFetchAiLogs] Trying to fetch log type: ${type || 'standard'} from ${url}`);
      const res = await fetch(url, { credentials: "include" });
      if (res.ok) {
        const logs = await res.text();
        console.log(`[handleFetchAiLogs] Successfully fetched log type: ${type || 'standard'}`);
        return { success: true, logs, type: type || 'standard' };
      }
      if (res.status === 404) {
        console.log(`[handleFetchAiLogs] Log type ${type || 'standard'} not found (404).`);
        return { success: false, status: 404 }; // Indicate not found
      }
      // Handle other errors
      const errorData = await res.text();
      console.error(`[handleFetchAiLogs] Error fetching log type ${type || 'standard'}: Status ${res.status}, Data: ${errorData}`);
      throw new Error(errorData || `Błąd pobierania logów (status ${res.status})`);
    };

    try {
      let result = await tryFetchLog('standard');

      if (!result.success && result.status === 404) {
        console.log("[handleFetchAiLogs] Standard log not found, trying critical...");
        result = await tryFetchLog('critical');
      }

      if (!result.success && result.status === 404) {
        console.log("[handleFetchAiLogs] Critical log not found, trying early...");
        result = await tryFetchLog('early');
      }

      if (result.success) {
        setCurrentLogs(`--- Logi (Typ: ${result.type}) ---\n\n${result.logs}`);
      } else {
        // If all attempts failed (last attempt was not 404 or was another error)
        throw new Error("Nie znaleziono żadnych logów (standardowych, krytycznych ani wczesnych) dla tej interakcji.");
      }

    } catch (err) {
      console.error("[handleFetchAiLogs] Final error after trying all types:", err);
      setCurrentLogs(`Nie udało się pobrać logów: ${err.message}`);
      // Keep the modal open but show error inside, hide loading spinner
      // showPopup({ title: "Błąd logów", message: `Nie udało się pobrać logów: ${err.message}`, type: 'error' }); // Avoid showing another popup if modal is already open
    } finally {
      setLogsLoading(false); // Reset loading state regardless of outcome
    }
  }, [backendUrl, setCurrentLogs, setLogsLoading, showPopup, uiState.showLogsModalConfig]); // Keep dependency on showLogsModalConfig in case it's used elsewhere, though logType is now handled internally

  // --- Add Missing Font ---
  const handleAddMissingFont = useCallback(async (fontName, fontFile) => {
      if (!fontName || !fontFile) return;

      showPopup({ title: "Dodawanie czcionki", message: "Przesyłanie...", type: 'loading' });
      const formData = new FormData();
      formData.append("fontName", fontName);
      formData.append("fontFile", fontFile);

      try {
          const res = await fetch(`${backendUrl}/admin/fonts`, {
              method: "POST",
              credentials: "include",
              body: formData
          });
          await handleApiResponse(res, "Czcionka została dodana.", "Błąd podczas dodawania czcionki", showPopup, hidePopup);
          // Refresh font list after adding
          dataUpdaters.fetchAllFonts();
          // Reset UI state for adding font (handled in component or UI hook)
          uiState.setMissingFontToAdd(null);
          uiState.setMissingFontFile(null);
      } catch (err) {
          console.error("[handleAddMissingFont]", err);
          hidePopup();
          showPopup({ title: "Błąd dodawania czcionki", message: err.message || "Nie udało się dodać czcionki.", type: 'error', size: 'small' });
      }
  }, [backendUrl, showPopup, hidePopup, dataUpdaters, uiState]); // Added dataUpdaters and uiState


  // --- Navigation ---
  const handleNavigateToEditor = useCallback((downloadKey) => {
      navigate(`/admin/orders/${orderId}/pdf-editor/${downloadKey}`);
  }, [navigate, orderId]);

  // --- Send Project Link to Client ---
  const handleSendProjectToClient = useCallback(async () => {
    // Poprawka: Sprawdzamy 'customOrderId' jako email klienta, zgodnie z danymi z backendu
    if (!order || !order.customOrderId || !order.publicAccessKey) {
      showPopup({ title: "Brak danych", message: "Nie można wysłać linku. Brakuje adresu e-mail klienta lub klucza publicznego zamówienia.", type: 'error', size: 'small' });
      return;
    }

    showPopup({ title: "Wysyłanie linku", message: "Proszę czekać...", type: 'loading' });

    try {
      const url = `${backendUrl}/admin/orders/${orderId}/send-project-link`; // Nowy endpoint
      const res = await fetch(url, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        // Można dodać body, jeśli potrzebne są dodatkowe dane, np. konkretny projekt
        // body: JSON.stringify({ projectId: '...' })
      });

      await handleApiResponse(res, "Link do projektu został wysłany do klienta.", "Błąd wysyłania linku", showPopup, hidePopup);

      // Można dodać odświeżenie danych zamówienia, jeśli backend zwraca zaktualizowane informacje
      // if (fetchOrderData) {
      //     fetchOrderData();
      // }

    } catch (err) {
      console.error("[handleSendProjectToClient]", err);
      hidePopup();
      showPopup({ title: "Błąd wysyłania", message: err.message || "Nie udało się wysłać linku do klienta.", type: 'error', size: 'small' });
    }
  }, [order, orderId, backendUrl, showPopup, hidePopup]); // Dodano zależności

  const handleSaveFileName = useCallback(async (fileId, newName) => {
    showPopup({ title: "Zapisywanie nazwy", message: "Proszę czekać...", type: 'loading' });

    const body = {
      fileName: newName
    };

    try {
      const res = await fetch(`${backendUrl}/admin/orders/${orderId}/files/${fileId}/rename`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body)
      });

      const result = await handleApiResponse(res, "Nazwa pliku została zaktualizowana.", "Błąd zapisu nazwy pliku", showPopup, hidePopup);

      if (result.files) {
        setFiles(result.files);
      }
    } catch (err) {
      console.error("[handleSaveFileName]", err);
      hidePopup();
      showPopup({ title: "Błąd zapisu", message: err.message || "Nie udało się zapisać nowej nazwy pliku.", type: 'error', size: 'small' });
    }
  }, [orderId, backendUrl, showPopup, hidePopup, setFiles]);

  // --- Copy Order Name ---
  const handleCopyOrderName = useCallback(() => {
    if (!order || !order.orderName) {
      showPopup({ 
        title: "Błąd", 
        message: "Brak nazwy zamówienia do skopiowania.", 
        type: 'error', 
        size: 'compact' 
      });
      return;
    }

    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      navigator.clipboard.writeText(order.orderName)
        .then(() => {
          showPopup({ 
            title: "Sukces", 
            message: "Nazwa zamówienia skopiowana do schowka", 
            type: 'success', 
            size: 'compact' 
          });
        })
        .catch(err => {
          console.error('[handleCopyOrderName] Clipboard API error: ', err);
          showPopup({ 
            title: "Błąd kopiowania", 
            message: "Nie udało się skopiować nazwy zamówienia.", 
            type: 'error', 
            size: 'compact' 
          });
        });
    } else {
      // Fallback
      try {
        const textArea = document.createElement("textarea");
        textArea.value = order.orderName;
        textArea.style.position = "fixed";
        textArea.style.left = "-9999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);

        if (successful) {
          showPopup({ 
            title: "Sukces", 
            message: "Nazwa zamówienia skopiowana do schowka", 
            type: 'success', 
            size: 'compact' 
          });
        } else {
          throw new Error('document.execCommand("copy") returned false');
        }
      } catch (err) {
        console.error('[handleCopyOrderName] Fallback execCommand error: ', err);
        showPopup({ 
          title: "Błąd kopiowania", 
          message: "Nie udało się skopiować nazwy zamówienia.", 
          type: 'error', 
          size: 'compact' 
        });
      }
    }
  }, [order, showPopup]);

  // --- Copy Email ---
  const handleCopyEmail = useCallback(() => {
    if (!order || !order.customOrderId) {
      showPopup({ 
        title: "Błąd", 
        message: "Brak emaila klienta do skopiowania.", 
        type: 'error', 
        size: 'compact' 
      });
      return;
    }

    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      navigator.clipboard.writeText(order.customOrderId)
        .then(() => {
          showPopup({ 
            title: "Sukces", 
            message: "Email klienta skopiowany do schowka", 
            type: 'success', 
            size: 'compact' 
          });
        })
        .catch(err => {
          console.error('[handleCopyEmail] Clipboard API error: ', err);
          showPopup({ 
            title: "Błąd kopiowania", 
            message: "Nie udało się skopiować emaila klienta.", 
            type: 'error', 
            size: 'compact' 
          });
        });
    } else {
      // Fallback
      try {
        const textArea = document.createElement("textarea");
        textArea.value = order.customOrderId;
        textArea.style.position = "fixed";
        textArea.style.left = "-9999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);

        if (successful) {
          showPopup({ 
            title: "Sukces", 
            message: "Email klienta skopiowany do schowka", 
            type: 'success', 
            size: 'compact' 
          });
        } else {
          throw new Error('document.execCommand("copy") returned false');
        }
      } catch (err) {
        console.error('[handleCopyEmail] Fallback execCommand error: ', err);
        showPopup({ 
          title: "Błąd kopiowania", 
          message: "Nie udało się skopiować emaila klienta.", 
          type: 'error', 
          size: 'compact' 
        });
      }
    }
  }, [order, showPopup]);

  return {
    handleSaveFileName,
    handleFileDropped,
    handleShowMetadata,
    handleGeneratePdf,
    handlePreviewPdf,
    handleSaveFileChanges,
    handleStatusChange,
    handleFetchGuestsForFile,
    handleSaveGuestsForFile,
    handleGeneratePersonalizedProject,
    handleDuplicateProject,
    handleAddProjectFromTemplate,
    handleDeleteProject,
    handleSaveEmail,
    handleCopyPublicLink,
    handleCopyOrderName,
    handleCopyEmail,
    handleAiRequestSubmit,
    handleFetchAiLogs,
    handleAddMissingFont,
    handleNavigateToEditor,
    handleSendProjectToClient, // Dodano nową akcję
  };
}
